/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  Column,
  EditableCallbackParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
} from '@ag-grid-community/core';
import { NewValueParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import { cloneDeep, differenceWith, isEqual, isNull } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { injectListBudgetAttributesQuery } from '@models/trial-attributes/list-budget-attributes-query';
import { injectPivotSettings } from '@models/trial-attributes/trial-attributes';

import { AgActionsComponentParams } from '@shared/ag-components/ag-actions/ag-actions.component';
import { AgRawTooltipValue } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ReasonModalData,
  ReasonModalModalComponent,
  ReasonModalResponse,
} from '@shared/components/modals/reason-modal/reason-modal.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { EDITABLE_CELL } from '@shared/constants/ag-classes';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  ActionType,
  Currency,
  EntityType,
  EventType,
  GqlService,
  listUserNamesWithEmailQuery,
  PrepaidType,
  TrialImplementationStatus,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { AgSetColumnsVisible, decimalAdd } from '@shared/utils';
import { decimalDifference } from '@shared/utils/floating-math';
import { injectClearQueryParams } from '@shared/utils/inject-clear-query-params';
import { ExportType, Utils } from '@shared/utils/utils';

import {
  AdjustmentsToPrepaidErrorRow,
  AdjustmentsToPrepaidRow,
  AdjustmentsToPrepaidTotals,
} from './adjustments-to-prepaid.model';
import {
  errorsToShowOverlay,
  getCauses,
  mapInitialGridValue,
  mapToAdjustmentToSave,
} from './adjustments-to-prepaid.utils';
import { adjustmentsToPrepaidGridConfig } from './grid-config/grid-config';
import { isTotalRow } from './grid-config/grid-config-context';
import { amountTypeMap } from './grid-config/prepaid-impact-column-group';
import { injectListInvoicesForPrepaidsQuery } from '../../models/list-invoices-for-prepaids-query';
import { injectListPrepaidsQuery } from '../../models/list-prepaids-query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    TableSkeletonComponent,
    ButtonComponent,
    TooltipDirective,
    ExportExcelButtonComponent,
    StickyGridDirective,
    TablePanelComponent,
  ],
  selector: 'aux-adjustments-to-prepaid',
  styleUrl: 'adjustments-to-prepaid.component.scss',
  templateUrl: 'adjustments-to-prepaid.component.html',
})
export class AdjustmentsToPrepaidComponent {
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  eventService = inject(EventService);
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);

  vendorId = input.required<string>();

  budgetAttributesQuery = injectListBudgetAttributesQuery({
    vendorId: this.vendorId,
  });
  clearQueryParams = injectClearQueryParams();
  flags = injectFlags({ journalEntryApManualClearing: 'journal_entry_ap_manual_clearing' });
  invoicesForPrepaidsQuery = injectListInvoicesForPrepaidsQuery({ vendorId: this.vendorId });
  permissions = injectPermissions({
    hasEditTerms: 'PERMISSION_UPDATE_PREPAIDS',
  });
  pivotSettingsQuery = injectPivotSettings();
  prepaidsQuery = injectListPrepaidsQuery({ vendorId: this.vendorId });

  addedGridValues = signal<string[]>([]);
  editMode = signal(false);
  errorRows = signal<Record<string, AdjustmentsToPrepaidErrorRow>>({});
  exportLoading = signal(false);
  gridAPI = signal<GridApi | null>(null);
  gridData = signal<AdjustmentsToPrepaidRow[]>([]);
  gridOptions = signal<GridOptions<AdjustmentsToPrepaidRow>>({});
  hasChanges = signal(false);
  initialGridValues = signal<AdjustmentsToPrepaidRow[]>([]);
  removedGridValues = signal<string[]>([]);
  // Map of removed row IDs to their delete reason for audit history
  removedReasons = signal<Record<string, string>>({});
  selectedRowIds = signal<string[]>([]);
  shouldStartFromEditMode = linkedSignal(() => {
    const { editMode } = this.route.snapshot.queryParams;

    if (editMode) {
      if (!this.isEditDisabled()) {
        // some signals depend on navigationStart event, so we need to wait for this computed context to be finished
        setTimeout(() => {
          void this.clearQueryParams({ editMode: null });
        }, 0);

        return true;
      }
    }

    return false;
  });
  showErrors = signal(false);
  totals = signal<AdjustmentsToPrepaidTotals>({
    investigator_amount: 0,
    invoice_total: 0,
    long_term_amount: 0,
    passthrough_amount: 0,
    services_amount: 0,
    short_term_amount: 0,
    total_expense_per_invoice: 0,
    total_impact_to_prepaid: 0,
  });

  canDeleteSelected = computed(() => this.selectedRowIds().length > 0);
  currentOpenMonth = computed(() => {
    const currentOpenMonth = this.mainQuery.getValue().currentOpenMonth;
    const openMonth = dayjs(currentOpenMonth);

    return openMonth.isValid() ? openMonth.format('YYYY-MM') : '';
  });
  isTotalsNegative = computed(() => {
    return (
      this.totals().short_term_amount < 0 ||
      this.totals().long_term_amount < 0 ||
      this.totals().services_amount < 0 ||
      this.totals().passthrough_amount < 0 ||
      this.totals().investigator_amount < 0
    );
  });
  loading = computed(
    () =>
      this.invoicesForPrepaidsQuery.isLoading() ||
      this.prepaidsQuery.isLoading() ||
      this.budgetAttributesQuery.isLoading() ||
      this.pivotSettingsQuery.loading()
  );
  prepaidLockTooltip = computed(() => {
    return !this.permissions().hasEditTerms
      ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
      : this.workflowQuery.prepaidLockTooltip();
  });

  isEditDisabled = input.required<boolean>();
  vendorCurrency = input.required<Currency>();
  vendorName = input.required<string>();

  adjustmentsSaved = output<void>();

  _syncGridOptions = effect(() => {
    const invoices = this.invoicesForPrepaidsQuery.data();
    const prepaids = this.prepaidsQuery.data();
    const vendorCurrency = this.vendorCurrency();
    const currentOpenMonth = this.currentOpenMonth();
    const pivotSettings = this.pivotSettingsQuery.data();
    const budgetAttributes = this.budgetAttributesQuery.data() || [];
    const flags = this.flags();

    untracked(() => {
      if (invoices && prepaids) {
        this.gridOptions.set(
          adjustmentsToPrepaidGridConfig(
            {
              budgetAttributes,
              checkError: this.checkError,
              currentOpenMonth,
              editMode: this.editMode,
              flags,
              getAuthor: this.getAuthor,
              getCellClasses: this.getCellClasses,
              invoiceOptions: invoices,
              isAdmin: this.authQuery.isAuxAdmin(),
              isCellEditable: this.isCellEditable,
              onCellValueChanged: this.onCellValueChanged,
              pivotSettings,
              recalculateCells: this.recalculateCells,
              status: this.mainQuery.getSelectedTrial()?.implementation_status,
              tooltipValueGetter: this.tooltipValueGetter,
              vendorCurrency,
            },
            this.overlayService
          )
        );

        this.initialGridValues.set(
          prepaids.map((adjustment) => mapInitialGridValue(adjustment, pivotSettings))
        );

        this.gridData.set(cloneDeep(this.initialGridValues()));
      }
    });
  });

  calculatePinnedBottomData(): AdjustmentsToPrepaidTotals {
    const gridApi = untracked(() => this.gridAPI());

    const totals: AdjustmentsToPrepaidTotals = {
      investigator_amount: 0,
      invoice_total: 0,
      long_term_amount: 0,
      passthrough_amount: 0,
      services_amount: 0,
      short_term_amount: 0,
      total_expense_per_invoice: 0,
      total_impact_to_prepaid: 0,
    };

    if (!gridApi) {
      return totals;
    }

    const columnsWithAggregation = [
      'total_impact_to_prepaid',
      'short_term_amount',
      'long_term_amount',
      'services_amount',
      'passthrough_amount',
      'investigator_amount',
      'invoice_total',
      'total_expense_per_invoice',
    ] as const;

    gridApi.forEachNodeAfterFilter(({ data }) => {
      for (const key of columnsWithAggregation) {
        const currentVal = totals[key];
        const additionalVal = data[key];

        if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
          totals[key] = decimalAdd(currentVal, additionalVal, 2);
        }
      }
    });

    return totals;
  }

  private checkChanges(): void {
    const currentValues: AdjustmentsToPrepaidRow[] = [];

    this.gridAPI()?.forEachNode(({ data }) => {
      currentValues.push(data);
    });

    this.hasChanges.set(!isEqual(this.initialGridValues(), currentValues));
  }

  checkError = (
    params: CellClassParams<AdjustmentsToPrepaidRow>,
    cause: AdjustmentsToPrepaidErrorRow['causes'][number]
  ): boolean => {
    if (!this.showErrors()) {
      return false;
    }

    const currentData = params.data;

    if (!currentData) return false;

    const errorRow = this.errorRows()[currentData.id];

    if (errorRow) {
      return errorRow.causes.includes(cause);
    }

    return false;
  };

  clearAll(): void {
    this.gridAPI()?.setFilterModel(null);
  }

  constructor() {
    effect(() => {
      this.editMode();
      const gridApi = untracked(() => this.gridAPI());

      if (gridApi) {
        gridApi.refreshHeader();
        gridApi.refreshCells({ force: true });
      }
    });
  }

  getAuthor = (id: string): listUserNamesWithEmailQuery => {
    return this.mainQuery.userMap().get(id || '') as listUserNamesWithEmailQuery;
  };

  getCellClasses =
    (classes: string[] = []) =>
    (params: CellClassParams) => {
      const cellClasses = [...classes];

      if (isTotalRow(params)) {
        return cellClasses;
      }

      if (this.editMode()) {
        if (params.column.isCellEditable(params.node) || params.node.rowPinned) {
          cellClasses.push(EDITABLE_CELL);
        } else {
          cellClasses.push('cursor-not-allowed');
        }
      }

      return cellClasses;
    };

  // initCurrentOpenMonth(): void {
  //   this.mainQuery
  //     .select('currentOpenMonth')
  //     .pipe(takeUntilDestroyed())
  //     // eslint-disable-next-line @typescript-eslint/no-misused-promises
  //     .subscribe(async (date) => {
  //       const openMonth = dayjs(date);
  //       this.currentOpenMonth = openMonth.isValid() ? openMonth.format('YYYY-MM') : '';
  //       const { editMode } = await firstValueFrom(this.route.queryParams);
  //       if (editMode) {
  //         if (!this.isEditDisabled()) {
  //           this.shouldStartFromEditMode = true;
  //           await this.router.navigate([], { queryParams: {}, replaceUrl: true });
  //         }
  //       }
  //     });
  // }
  isCellEditable = (
    params: AgActionsComponentParams | EditableCallbackParams<AdjustmentsToPrepaidRow>
  ): boolean => {
    const status = this.mainQuery.getSelectedTrial()?.implementation_status;
    const openMonth = this.mainQuery.getValue().currentOpenMonth;
    const initialRowData = this.initialGridValues().find(
      (row: AdjustmentsToPrepaidRow) => row.id === params.data?.id
    );

    if (!this.editMode()) {
      return false;
    }

    if (!params.data || !status || !params.column) {
      return true;
    }

    if (params.data.invoice_id) {
      if (
        params.column.getColId() === 'accrual_period' ||
        params.column.getColId() === 'invoice_total' ||
        params.column.getColId() === 'invoice_date' ||
        params.column.getColId() === 'payment_date'
      ) {
        return false;
      }
    }

    if (!initialRowData) {
      return true;
    }

    if (
      status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
      status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
    ) {
      if (params.column.getColId() === 'payment_date') {
        return !(
          initialRowData.payment_date && dayjs(initialRowData.payment_date).isBefore(openMonth)
        );
      }

      if (
        initialRowData.accrual_period &&
        dayjs(initialRowData.accrual_period).isBefore(openMonth)
      ) {
        return false;
      }
    }

    return true;
  };

  onAddAdjustment = (): void => {
    const gridApi = untracked(() => this.gridAPI());

    if (!gridApi) {
      return;
    }

    // clear filters
    gridApi.setFilterModel(null);

    this.editMode.set(true);
    this.onEditModeOn();
    const newAdjustment = <AdjustmentsToPrepaidRow>{
      create_date: dayjs().toISOString(),
      created_by: this.authQuery.getValue().sub,
      id: Utils.uuid(),
      isNewAdjustment: true,
      pivot_column: [] as string[],
      prepaid_type: null,
    };

    const transaction = gridApi.applyTransaction({
      add: [
        {
          ...newAdjustment,
        },
      ],
      addIndex: 0,
    });

    this.errorRows.update((errors) => {
      return {
        ...errors,
        [newAdjustment.id]: {
          adjustment: newAdjustment,
          causes: ['prepaid_type'],
        },
      };
    });

    setTimeout(() => {
      if (transaction?.add.length) {
        gridApi.startEditingCell({
          colKey: 'pivot_column',
          rowIndex: 0,
        });
      }
    }, TableConstants.ANIMATION_TIME);

    this.checkChanges();
    this.addedGridValues.update((values) => [...values, newAdjustment.id]);
  };

  onCancel = (): void => {
    this.editMode.set(false);
    this.gridData.set(cloneDeep(this.initialGridValues()));
    setTimeout(() => this.setBottomData());
    this.onEditModeOff();
  };

  onCellValueChanged = (params: NewValueParams): void => {
    const currentData = params.data as AdjustmentsToPrepaidRow;
    const openMonth = this.mainQuery.getValue().currentOpenMonth;
    const status = this.mainQuery.getSelectedTrial()?.implementation_status;

    const causes = getCauses({
      currentData,
      initialGridValues: this.initialGridValues(),
      openMonth,
      status,
    });

    this.setBottomData();

    if (causes.length > 0) {
      this.errorRows.update((errors) => {
        return {
          ...errors,
          [currentData.id]: { adjustment: currentData, causes },
        };
      });
    } else {
      this.errorRows.update((state) => {
        const { [currentData.id]: _, ...rest } = state;

        return {
          ...rest,
        };
      });
    }

    if (params.node) {
      params.api.refreshCells({
        force: true,
        rowNodes: [params.node],
      });
    }

    this.checkChanges();
  };

  onDeleteSelected = async () => {
    const selectedIds = this.selectedRowIds();

    if (!selectedIds.length) return;

    const message = `Are you sure you want to delete selected prepaid adjustments?`;
    const ref = this.overlayService.openPopup<
      ReasonModalData,
      ReasonModalResponse,
      ReasonModalModalComponent
    >({
      content: ReasonModalModalComponent,
      data: { message },
      settings: {
        header: 'Delete Prepaid Adjustments?',
        primaryButton: {
          action: (instance: null | ReasonModalModalComponent) => instance?.save(),
          label: 'Delete',
          variant: 'negative',
        },
        secondaryButton: {
          label: 'Cancel',
        },
      },
    });
    const event = await firstValueFrom(ref.afterClosed$);

    if (event?.data) {
      const reason = event.data.reason;
      const gridApi = this.gridAPI();

      if (gridApi) {
        const nodesToRemove = gridApi
          .getSelectedNodes()
          .filter((node) => selectedIds.includes(node.data?.id));

        nodesToRemove.forEach((node) => {
          const isNew = node.data.isNewAdjustment || false;

          if (!isNew) {
            if (!this.removedGridValues().includes(node.data.id)) {
              this.removedGridValues().push(node.data.id);
            }

            // Store the reason for this row
            this.removedReasons.update((reasons) => ({ ...reasons, [node.data.id]: reason }));
          }

          // Remove the error row
          this.errorRows.update((errors) => {
            const { [node.data.id]: _, ...rest } = errors;

            return rest;
          });
        });
        gridApi.applyTransaction({ remove: nodesToRemove.map((node) => node.data) });
        this.selectedRowIds.set([]);
      }

      this.checkChanges();
      this.setBottomData();
    }
  };

  onEdit = (): void => {
    const gridApi = untracked(() => this.gridAPI());

    if (!gridApi) {
      return;
    }

    this.editMode.set(true);
    this.onEditModeOn();
    const firstItemWithoutPivotColumn = gridApi
      .getRenderedNodes()
      .find((node) => !node.data.pivot_column?.length);
    const rowIndex =
      firstItemWithoutPivotColumn && !isNull(firstItemWithoutPivotColumn.rowIndex)
        ? firstItemWithoutPivotColumn.rowIndex
        : gridApi.getRenderedNodes().length - 1;

    setTimeout(() => {
      gridApi.startEditingCell({
        colKey: 'pivot_column',
        rowIndex: rowIndex,
      });
    }, TableConstants.ANIMATION_TIME);
  };

  private onEditModeOff(): void {
    this.showErrors.set(false);
    const gridApi = untracked(() => this.gridAPI());

    if (!gridApi) {
      return;
    }

    gridApi.stopEditing();
    AgSetColumnsVisible({
      gridApi,
      keys: ['selection'],
      visible: false,
    });
    gridApi.refreshCells({ force: true });
    gridApi.sizeColumnsToFit();
    this.addedGridValues.set([]);
    this.removedGridValues.set([]);
    this.removedReasons.set({});
    this.selectedRowIds.set([]);
    this.errorRows.set({});
    this.hasChanges.set(false);
  }

  private onEditModeOn(): void {
    const gridApi = untracked(() => this.gridAPI());

    if (!gridApi) {
      return;
    }

    AgSetColumnsVisible({
      gridApi,
      keys: ['selection'],
      visible: true,
    });
    gridApi.sizeColumnsToFit();

    const currentGridData: AdjustmentsToPrepaidRow[] = [];

    gridApi.forEachNode(({ data }) => {
      currentGridData.push(data);
    });

    // need to trigger rowData change to re-calculate isRowSelectable for all rows
    gridApi.updateGridOptions({
      rowData: cloneDeep(currentGridData),
    });
  }

  onExport = async () => {
    const filter_model = this.gridAPI()!.getFilterModel();
    const allColumnsState = this.gridAPI()!.getColumnState?.() ?? [];
    const sort_model = allColumnsState
      .filter((col) => col.sort)
      .map((col) => ({
        column: col.colId,
        descending: col.sort === 'desc',
      }));
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.exportLoading()) {
      return;
    }

    this.exportLoading.set(true);

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: this.vendorId() || '',
          export_type: ExportType.PREPAID_DETAIL,
          filename: `${trialName}_${this?.vendorName()}_auxilius-prepaids_${dateStr}`,
          filter_model,
          sort_model,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.exportLoading.set(false);
  };

  onFilterChanged(): void {
    this.setBottomData();
  }

  onGridReady({ api }: GridReadyEvent): void {
    this.gridAPI.set(api);
    api.sizeColumnsToFit();
    this.setBottomData();

    if (this.shouldStartFromEditMode()) {
      this.onEdit();
      this.shouldStartFromEditMode.set(false);
    }
  }

  onSaveAll = async (): Promise<void> => {
    const errorMessages = errorsToShowOverlay(this.errorRows(), this.pivotSettingsQuery.data());

    this.showErrors.set(true);

    if (errorMessages.length > 0) {
      this.overlayService.error(errorMessages, undefined, true);

      return;
    }

    const pivotSettings = this.pivotSettingsQuery.data();
    const currentValues: AdjustmentsToPrepaidRow[] = [];

    this.gridAPI()!.forEachNode(({ data }) => {
      currentValues.push({ ...data });
    });

    // --- Per-attribute/cost category negative balance validation ---
    const negativeAttrErrors = this.validateNegativePrepaidBalances(currentValues);

    if (negativeAttrErrors.length > 0) {
      this.overlayService.error(negativeAttrErrors, undefined, true);

      return;
    }

    const changedRows = differenceWith(currentValues, this.initialGridValues(), (obj1, obj2) => {
      return isEqual(obj1, obj2);
    });

    const adjustmentsToSave = changedRows.map((row) =>
      mapToAdjustmentToSave({
        actionType: row.isNewAdjustment
          ? ActionType.ACTION_TYPE_CREATE
          : ActionType.ACTION_TYPE_UPDATE,
        pivotSettings,
        removedRowId: '',
        row,
        vendorId: this.vendorId(),
      })
    );

    const removedReasons = this.removedReasons();

    // Pass the reason for each removed row to the audit log
    const removedAdjustments = this.removedGridValues().map((id) =>
      mapToAdjustmentToSave({
        actionType: ActionType.ACTION_TYPE_REMOVE,
        pivotSettings,
        reason: removedReasons[id],
        removedRowId: id,
        row: {} as AdjustmentsToPrepaidRow,
        vendorId: this.vendorId(),
      })
    );

    const result = await firstValueFrom(
      this.gqlService.batchModifyPrepaids$([...adjustmentsToSave, ...removedAdjustments])
    );

    if (result.success) {
      await Promise.all([this.prepaidsQuery.refetch(), this.invoicesForPrepaidsQuery.refetch()]);
      this.overlayService.success(MessagesConstants.SAVED);
      this.adjustmentsSaved.emit();
      this.onEditModeOff();
      this.editMode.set(false);
    } else if (result.errors.length) {
      this.overlayService.error(result.errors);
    }
  };

  onSelectionChanged(): void {
    const gridApi = this.gridAPI();

    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes();

    this.selectedRowIds.set(selectedNodes.map((node) => node.data?.id).filter(Boolean));
  }

  onSortChanged() {
    const gridApi = this.gridAPI();

    if (!gridApi) return;

    // Check current sort state directly from gridApi
    const currentSortModel = (gridApi.getColumnState() || []).filter((cs) => cs.sort);

    // If no sort is applied, set the default multi-sort: accrual_period desc, invoice_id asc, pivot_column asc
    if (!currentSortModel || currentSortModel.length === 0) {
      gridApi.applyColumnState({
        state: [
          { colId: 'accrual_period', sort: 'desc', sortIndex: 0 },
          { colId: 'invoice_id', sort: 'asc', sortIndex: 1 },
          { colId: 'pivot_column', sort: 'asc', sortIndex: 2 },
        ],
      });
    }
  }

  recalculateCells = (params: NewValueParams): void => {
    params.data.total_impact_to_prepaid = decimalAdd(
      params.data.short_term_amount || 0,
      params.data.long_term_amount || 0,
      2
    );

    if (params.data.prepaid_type === PrepaidType.PREPAID_TYPE_AMORTIZE) {
      params.data.total_expense_per_invoice = decimalDifference(
        params.data.invoice_total,
        params.data.total_impact_to_prepaid,
        2
      );
    } else {
      params.data.total_expense_per_invoice = params.data.invoice_total;
    }
  };

  setBottomData(): void {
    const gridApi = untracked(() => this.gridAPI());

    if (!gridApi) {
      return;
    }

    this.totals.set(this.calculatePinnedBottomData());

    gridApi.setGridOption('pinnedBottomRowData', [
      {
        pivot_column: 'Total',
        ...this.totals(),
      },
    ]);
  }

  tooltipValueGetter = (
    params: ITooltipParams<AdjustmentsToPrepaidRow>
  ): AgRawTooltipValue | string => {
    if (params.node && this.editMode()) {
      const column = params.column as Column;
      const status = this.mainQuery.getSelectedTrial()?.implementation_status;
      const initialRowData = this.initialGridValues().find(
        (row: AdjustmentsToPrepaidRow) => row.id === params.data?.id
      );
      const openMonth = this.mainQuery.getValue().currentOpenMonth;

      if (!initialRowData || column.isCellEditable(params.node)) {
        return '';
      }

      if (params.data?.invoice_id) {
        if (
          column.getColId() === 'accrual_period' ||
          column.getColId() === 'invoice_total' ||
          column.getColId() === 'invoice_date' ||
          column.getColId() === 'payment_date'
        ) {
          return { forceTooltip: true, value: MessagesConstants.PREPAIDS.INVOICE_POPULATED };
        }
      }

      if (
        status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
        status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
      ) {
        if (column.getColId() === 'payment_date') {
          // eslint-disable-next-line max-depth
          if (
            initialRowData.payment_date &&
            dayjs(initialRowData.payment_date).isBefore(openMonth)
          ) {
            return {
              forceTooltip: true,
              value: MessagesConstants.PREPAIDS.PAYMENT_DATE_IN_CLOSED_MONTH_TOOLTIP,
            };
          }

          return '';
        }

        if (
          initialRowData.accrual_period &&
          dayjs(initialRowData.accrual_period).isBefore(openMonth)
        ) {
          return {
            forceTooltip: true,
            value: MessagesConstants.PREPAIDS.ADJUSTMENT_IN_CLOSED_MONTH,
          };
        }
      }
    }

    return '';
  };

  validateNegativePrepaidBalances(currentValues: AdjustmentsToPrepaidRow[]): string[] {
    const pivotSettings = this.pivotSettingsQuery.data();

    // Group by attribute/cost category (pivot_column)
    const totalsByAttr: Record<string, { longTerm: number; shortTerm: number }> = {};

    currentValues.forEach((row) => {
      const key = row.pivot_column?.join(' / ') || 'Unknown';

      if (!totalsByAttr[key]) {
        totalsByAttr[key] = { longTerm: 0, shortTerm: 0 };
      }

      totalsByAttr[key].shortTerm = decimalAdd(
        Number(row.short_term_amount) || 0,
        totalsByAttr[key].shortTerm || 0,
        2
      );

      totalsByAttr[key].longTerm = decimalAdd(
        Number(row.long_term_amount) || 0,
        totalsByAttr[key].longTerm || 0,
        2
      );
    });

    // If any attribute/cost category total goes below zero, block save
    const negativeAttrErrors: string[] = [];
    const attrLabel = pivotSettings.primary?.attribute_name || 'Cost Category';
    const secondaryLabel = pivotSettings.secondary?.attribute_name;
    const fullLabel = secondaryLabel ? `${attrLabel} / ${secondaryLabel}` : attrLabel;
    const isCostCategoryView = !pivotSettings.primary?.attribute_name;

    Object.entries(totalsByAttr).forEach(([attr, totals]) => {
      let displayAttr = attr;

      if (isCostCategoryView && attr in amountTypeMap) {
        displayAttr = amountTypeMap[attr as keyof typeof amountTypeMap];
      }

      if (totals.shortTerm < 0) {
        negativeAttrErrors.push(
          `Prepaid Balance cannot be below zero for any ${fullLabel} value (Short Term): ${displayAttr}`
        );
      }

      if (totals.longTerm < 0) {
        negativeAttrErrors.push(
          `Prepaid Balance cannot be below zero for any ${fullLabel} value (Long Term): ${displayAttr}`
        );
      }
    });

    return negativeAttrErrors;
  }
}
